import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Link,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import MediaCard from '../profile/analytics/MediaCard';
import MediaAnalyticsModal from '../profile/analytics/MediaAnalyticsModal';
import AccountAnalytics from '../profile/analytics/AccountAnalytics';
import MediaAnalytics from '../profile/analytics/MediaAnalytics';
import {
  fetchInfluencerDetail,
  resolveInfluencerPayload,
} from '../../_services/influencers/influencer.service';
import instagramIcon from '../../img/socialHandles/instagram.png';
import facebookIcon from '../../img/socialHandles/facebook2.png';
import youtubeIcon from '../../img/socialHandles/youtube.png';
import './influencers.css';
import { useHistory } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

const resolveDetailObject = (payload) => {
  if (!payload) return {};
  if (Array.isArray(payload)) return payload[0] || {};
  if (payload?.data && !Array.isArray(payload.data)) return payload.data;
  return payload;
};

const getInfluencerRecord = (detail = {}) => detail?.influencer || detail;

const normalizePlatform = (row = {}) => {
  const value = String(
    row?.platform || row?.accountType || row?.AccountType || row?.platform_name || ''
  ).toLowerCase();

  if (value.includes('youtube')) return 'youtube';
  if (value.includes('facebook') || value === 'page') return 'facebook';
  return 'instagram';
};

const normalizeBasicInfo = (detail = {}) => {
  const influencer = getInfluencerRecord(detail);
  const platform = normalizePlatform(influencer);
  return {
    handle:
      influencer?.username ||
      influencer?.instagram_username ||
      influencer?.instagram_account ||
      'Not available',
    bio: influencer?.biography || influencer?.bio || 'No bio available.',
    profileImage:
      influencer?.profile_picture_url ||
      influencer?.profile_image ||
      detail?.profile_picture_url ||
      detail?.profile_pic ||
      detail?.profile_picture ||
      detail?.thumbnail_url ||
      '',
    followers:
      influencer?.followers ||
      influencer?.followers_count ||
      influencer?.follower_count,
    platform,
    pageName: influencer?.page_name || '',
  };
};

const normalizeMediaRows = (detail = {}) => {
  const source =
    detail?.media ||
    detail?.latest_media ||
    detail?.media_analytics ||
    detail?.posts ||
    detail?.results ||
    [];

  if (!Array.isArray(source)) {
    return [];
  }

  return source.map((item, index) => ({
    ...item,
    id: item?.id || item?.media_id || item?.permalink || index,
    thumbnail_url: item?.thumbnail_url || item?.media_url || '',
    media_url: item?.media_url || item?.thumbnail_url || '',
    likes: item?.likes || item?.like_count || 0,
    reach: item?.reach || 0,
    engagement: item?.engagement || item?.engagement_rate || 0,
    saves: item?.saves || 0,
    comments: item?.comments || item?.comment_count || 0,
    shares: item?.shares || 0,
  }));
};

const formatCount = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? new Intl.NumberFormat('en-US').format(number) : '-';
};

const formatUrlLabel = (url = '') =>
  String(url)
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '');

const buildOverviewFields = (detail = {}) => {
  const influencer = getInfluencerRecord(detail);
  const username = influencer?.username || influencer?.instagram_username || '';

  return [
    {
      label: 'Account Type',
      value: influencer?.account_type || influencer?.accountType || '-',
      capitalize: true,
    },
    { label: 'Username', value: username ? `@${username}` : '-' },
    { label: 'Page Name', value: influencer?.page_name || '-' },
    {
      label: 'Followers',
      value: formatCount(
        influencer?.followers_count ?? influencer?.followers ?? influencer?.follower_count
      ),
    },
    { label: 'Media Count', value: formatCount(influencer?.media_count) },
    { label: 'Website', value: influencer?.website || '', isLink: true },
    {
      label: 'Page URL',
      value: influencer?.profile_url || influencer?.page_url || '',
      isLink: true,
    },
  ];
};

const InfluencerDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [detail, setDetail] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const history = useHistory();

  useEffect(() => {
    let isMounted = true;

    const loadDetail = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetchInfluencerDetail(id);

        if ((response?.status || 500) >= 400) {
          throw new Error('Unable to load influencer details.');
        }

        if (!isMounted) return;
        const payload = resolveInfluencerPayload(response);
        setDetail(resolveDetailObject(payload));
      } catch (fetchError) {
        if (!isMounted) return;
        setError('Failed to load influencer details. Please try again.');
        setDetail({});
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadDetail();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const basicInfo = useMemo(() => normalizeBasicInfo(detail), [detail]);
  const overviewFields = useMemo(() => buildOverviewFields(detail), [detail]);
  const accountAnalytics = useMemo(() => detail?.account_analytics || {}, [detail]);
  const mediaAnalyticsRows = useMemo(
    () => normalizeMediaRows(detail?.media_analytics || {}),
    [detail]
  );
  const latestMedia = useMemo(() => mediaAnalyticsRows.slice(0, 3), [mediaAnalyticsRows]);

  const platformIcon = {
    instagram: instagramIcon,
    facebook: facebookIcon,
    youtube: youtubeIcon,
  }[basicInfo.platform] || instagramIcon;

  return (
    <Box className="influencer-page-shell">
      <Typography sx={{fontWeight: '600', fontSize:"18px",cursor: 'pointer', pb:1}} onClick={() => history.push("/influencers")}>
        <ArrowBackIcon /> Back to Influencers list
      </Typography>
      {loading ? (
        <Box className="influencer-loading-shell">
          <CircularProgress />
        </Box>
      ) : null}

      {!loading && error ? <Alert severity="error">{error}</Alert> : null}

      {!loading && !error ? (
        <>
          <Box className="influencer-detail-hero">
            <Box className="influencer-detail-summary">
              <Avatar
                src={basicInfo.profileImage}
                alt={basicInfo.handle}
                className="influencer-detail-avatar"
              >
                {basicInfo.handle?.charAt?.(0)?.toUpperCase() || 'I'}
              </Avatar>

              <Box>
                <Box className="influencer-handle-row">
                  <img src={platformIcon} alt={basicInfo.platform} className="influencer-handle-icon" />
                  <Typography variant="h5" className="influencer-detail-title">
                    {basicInfo.handle}
                  </Typography>
                </Box>
                <Typography className="influencer-detail-bio">{basicInfo.bio}</Typography>
              </Box>
            </Box>
          </Box>

          <Box className="influencer-detail-tabs">
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              className="influencer-tabs"
              TabIndicatorProps={{ sx: { display: 'none' } }}
            >
              <Tab
                className="influencer-tab"
                icon={<PersonOutlineOutlinedIcon fontSize="small" />}
                iconPosition="start"
                label="Overview"
              />
              <Tab
                className="influencer-tab"
                icon={<InsightsOutlinedIcon fontSize="small" />}
                iconPosition="start"
                label="Account Analytics"
              />
              <Tab
                className="influencer-tab"
                icon={<GridViewOutlinedIcon fontSize="small" />}
                iconPosition="start"
                label="Media Analytics"
              />
            </Tabs>
          </Box>

          {activeTab === 0 ? (
            <Box className="influencer-overview-grid">
              <Box className="influencer-section-card">
                <Typography variant="h6" className="influencer-section-title">
                  Influencer Overview
                </Typography>
                <Box className="influencer-overview-list">
                  {overviewFields.map((field) => (
                    <Box key={field.label} className="influencer-overview-row">
                      <Typography className="influencer-overview-label">{field.label}</Typography>

                      {field.isLink && field.value ? (
                        <Link
                          href={field.value}
                          target="_blank"
                          rel="noreferrer"
                          title={field.value}
                          className="influencer-overview-link"
                        >
                          <span className="influencer-overview-link-text">
                            {formatUrlLabel(field.value)}
                          </span>
                          <OpenInNewOutlinedIcon fontSize="inherit" />
                        </Link>
                      ) : (
                        <Typography
                          title={String(field.value)}
                          className={`influencer-overview-value${field.capitalize ? ' caps' : ''}`}
                        >
                          {field.value || '-'}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box className="influencer-section-card">
                <Box className="influencer-section-header">
                  <Typography variant="h6" className="influencer-section-title">
                    Latest Media
                  </Typography>
                  <button
                    type="button"
                    className="influencer-view-more-btn"
                    onClick={() => setActiveTab(2)}
                  >
                    View More
                  </button>
                </Box>
                {latestMedia.length ? (
                  <Box className="influencer-latest-media-grid">
                    {latestMedia.map((media, index) => (
                      <MediaCard
                        key={media?.id || media?.permalink || index}
                        media={media}
                        loading={false}
                        onClick={setSelectedMedia}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography className="influencer-detail-bio">
                    No recent media is available in the detail response.
                  </Typography>
                )}
              </Box>

              <MediaAnalyticsModal
                open={Boolean(selectedMedia)}
                media={selectedMedia}
                onClose={() => setSelectedMedia(null)}
              />
            </Box>
          ) : null}

          {activeTab === 1 ? (
            <AccountAnalytics
              selectedHandleId={id}
              overrideAnalyticsData={accountAnalytics}
              hideFilters
              helperText="Account-level insights from the influencer detail response."
            />
          ) : null}
          {activeTab === 2 ? (
            <MediaAnalytics
              selectedHandleId={id}
              overrideMediaRows={mediaAnalyticsRows}
              hideFilters
            />
          ) : null}
        </>
      ) : null}
    </Box>
  );
};

export default InfluencerDetail;
